import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      console.error('Auth check failed:', e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const sendOTP = async (email) => {
    const response = await axios.post(`${API}/auth/send-otp`, { email });
    return response.data;
  };

  const verifyOTP = async (email, otp) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
    
    if (response.data.requires_registration) {
      return { requiresRegistration: true, email };
    }
    
    // Token is now set as httpOnly cookie by the backend
    setUser(response.data.user);
    return { success: true, user: response.data.user };
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const data = response.data;
    
    // Cookie is set by backend for auto-approved users
    if (data.is_active) {
      setUser(data);
    }
    
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    sendOTP,
    verifyOTP,
    register,
    logout,
    API,
    BACKEND_URL
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
